import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// Import only the necessary Polaris components for a minimal UI
import { Spinner } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';

/**
 * Dedicated page to handle OAuth callbacks and ensure proper popup window closure
 * This page handles the Google Drive OAuth flow completion and communicates
 * back to the parent window
 */
export default function OAuthCallback() {
  const location = useLocation();
  const { user } = useAuth();
  const [status, setStatus] = React.useState('processing');
  const [message, setMessage] = React.useState('Processing authentication...');
  const [error, setError] = React.useState(null);
  
  // Set document title and ensure this page takes over the entire window
  useEffect(() => {
    // Set page title
    document.title = 'Google Drive Authentication';
    
    // Add a class to the body to ensure this page is full-screen
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    
    // Remove any potential app layout elements (safety measure)
    const appElements = document.querySelectorAll('.Polaris-Frame, .Polaris-Navigation');
    appElements.forEach(el => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    
    return () => {
      // Clean up when component unmounts
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const encodedTokens = params.get('tokens');
        const success = params.get('googleDriveSuccess');
        const errorMsg = params.get('googleDriveError');
        
        console.log('OAuth Callback - Processing parameters', { 
          success,
          hasTokens: encodedTokens ? 'Yes' : 'No',
          errorMsg
        });
        
        // Handle error cases first
        if (errorMsg) {
          setStatus('error');
          setError(`Authentication failed: ${errorMsg}`);
          notifyParentAndClose(false, `Authentication failed: ${errorMsg}`);
          return;
        }
        
        // Handle successful OAuth with tokens
        if (success === 'true' && encodedTokens) {
          setMessage('Authentication successful, saving credentials...');
          
          try {
            // Decode the base64 encoded tokens
            let base64Str = encodedTokens;
            // Replace URL-safe chars with standard base64 chars
            base64Str = base64Str.replace(/-/g, '+').replace(/_/g, '/');
            // Add padding if needed
            while (base64Str.length % 4) {
              base64Str += '=';
            }
            // Now decode
            const tokensJson = atob(base64Str);
            const tokens = JSON.parse(tokensJson);
            
            // Verify token has all required fields
            console.log('Token validation check:', {
              has_access_token: !!tokens.access_token,
              has_refresh_token: !!tokens.refresh_token,
              has_expiry: !!tokens.expiry_date,
              has_scope: !!tokens.scope,
              scope_value: tokens.scope || 'MISSING',
              token_type: tokens.token_type || 'MISSING'
            });
            
            // Validate all required token fields
            const missingFields = [];
            if (!tokens.access_token) missingFields.push('access_token');
            if (!tokens.refresh_token) missingFields.push('refresh_token');
            if (!tokens.expiry_date) missingFields.push('expiry_date');
            if (!tokens.scope) missingFields.push('scope');
            
            if (missingFields.length > 0) {
              throw new Error(`Invalid token: missing ${missingFields.join(', ')}`);
            }
            
            // Verify required scopes are present
            const requiredScopes = ['https://www.googleapis.com/auth/drive.file'];
            const tokenScopes = tokens.scope.split(' ');
            const missingScopes = requiredScopes.filter(scope => !tokenScopes.includes(scope));
            
            if (missingScopes.length > 0) {
              console.warn('Token is missing some recommended scopes:', missingScopes);
              // We'll continue anyway but log the warning
            }
            
            // Save tokens to Firestore if user is authenticated
            if (user && user.uid) {
              // Reference to the user's document
              const userDocRef = doc(db, 'users', user.uid);
              
              // Prepare Google Drive data
              const googleDriveData = {
                tokens,
                enabled: true,
                connected: true,
                folderNamingOption: 'productName', // Default value
                mainFolder: 'Editify Product Images', // Default value
                lastUpdated: new Date().toISOString()
              };
              
              // Update the user document with Google Drive data
              await updateDoc(userDocRef, {
                googleDrive: googleDriveData
              });
              
              console.log('Successfully saved Google Drive tokens to Firestore');
              setStatus('success');
              setMessage('Google Drive connected successfully! Closing window...');
              
              // Notify parent window and close this popup
              notifyParentAndClose(true);
            } else {
              setStatus('error');
              setError('You must be logged in to connect Google Drive');
              notifyParentAndClose(false, 'Authentication error: User not logged in');
            }
          } catch (error) {
            console.error('Error processing OAuth callback:', error);
            setStatus('error');
            setError(`Error: ${error.message}`);
            notifyParentAndClose(false, `Error: ${error.message}`);
          }
        } else {
          setStatus('error');
          setError('Invalid or missing authentication data');
          notifyParentAndClose(false, 'Invalid or missing authentication data');
        }
      } catch (error) {
        console.error('Unexpected error in OAuth callback:', error);
        setStatus('error');
        setError(`Unexpected error: ${error.message}`);
        notifyParentAndClose(false, `Unexpected error: ${error.message}`);
      }
    };
    
    processOAuthCallback();
  }, [location, user]);
  
  /**
   * Notify the parent window about authentication result and close this popup
   */
  const notifyParentAndClose = (success, errorMessage = null) => {
    // Try multiple communication methods to ensure the message gets through
    try {
      // Get parent origin if available
      let parentOrigin = '*';
      try {
        const storedOrigin = localStorage.getItem('parentOrigin');
        if (storedOrigin) {
          parentOrigin = storedOrigin;
          console.log('Using stored parent origin:', parentOrigin);
        }
      } catch (e) {
        console.warn('Could not get parent origin from localStorage');
      }
      
      // Method 1: postMessage API
      if (window.opener) {
        window.opener.postMessage({
          type: 'GOOGLE_DRIVE_CONNECTED',
          success,
          errorMessage
        }, parentOrigin);
        console.log('Sent postMessage to parent window');
      }
      
      // Method 2: Try to call parent's callback directly
      if (window.opener && window.opener.googleDriveCallback) {
        window.opener.googleDriveCallback({
          success,
          errorMessage
        });
        console.log('Called parent callback directly');
      }
      
      // Method 3: Store in localStorage as fallback
      if (success) {
        localStorage.setItem('googleDriveConnected', 'true');
        localStorage.setItem('googleDriveConnectedTime', new Date().toISOString());
        localStorage.setItem('googleDriveConnectedSource', 'oauth-callback-page');
      }
      
      // Close this window after a short delay to ensure message is sent and user sees the result
      setTimeout(() => {
        try {
          window.close();
        } catch (e) {
          console.warn('Could not close window automatically');
          // Show a message to manually close if automatic closing fails
          setMessage(success 
            ? 'Google Drive connected successfully! You can close this window now.' 
            : `Authentication failed: ${errorMessage}. You can close this window now.`
          );
        }
      }, success ? 1000 : 2000); // Close faster on success, give a bit more time to read errors
    } catch (error) {
      console.error('Error in notifyParentAndClose:', error);
      // If all communication methods fail, show a message to manually close
      setMessage(success 
        ? 'Google Drive connected successfully! You can close this window now.' 
        : `Authentication failed: ${errorMessage}. You can close this window now.`
      );
    }
  };
  
  // Render a clean standalone UI for the popup window
  return (
    <div style={{
      padding: '20px',
      textAlign: 'center',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      width: '100%',
      height: '100vh',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f6f6f7',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    }}>
      {/* App logo */}
      <div style={{ marginBottom: '24px' }}>
        <img 
          src="/logo.png" 
          alt="Editify" 
          style={{ height: '40px', width: 'auto' }} 
        />
      </div>
      
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        width: '90%',
        maxWidth: '450px',
      }}>
        <h1 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          marginBottom: '20px', 
          color: '#212b36' 
        }}>
          Google Drive Authentication
        </h1>
        
        {status === 'processing' && (
          <div style={{ 
            margin: '30px auto', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flexDirection: 'column' 
          }}>
            <Spinner size="large" />
            <p style={{ 
              marginTop: '15px', 
              fontSize: '16px', 
              color: '#637381' 
            }}>
              {message}
            </p>
          </div>
        )}
        
        {status === 'success' && (
          <div style={{
            padding: '15px',
            backgroundColor: '#f1f8f5',
            borderLeft: '4px solid #00848e',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px' 
            }}>
              <svg width="22" height="22" viewBox="0 0 20 20" fill="none" style={{ marginRight: '8px' }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z" fill="#00848e" />
              </svg>
              <h2 style={{ 
                margin: '0',
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#00848e' 
              }}>Connected Successfully</h2>
            </div>
            <p style={{ 
              margin: '0',
              color: '#212b36',
              fontSize: '14px' 
            }}>{message}</p>
          </div>
        )}
        
        {status === 'error' && (
          <div style={{
            padding: '15px',
            backgroundColor: '#fbeae5',
            borderLeft: '4px solid #d72c0d',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px' 
            }}>
              <svg width="22" height="22" viewBox="0 0 20 20" fill="none" style={{ marginRight: '8px' }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L8.58579 10L7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071C7.68342 13.0976 8.31658 13.0976 8.70711 12.7071L10 11.4142L11.2929 12.7071C11.6834 13.0976 12.3166 13.0976 12.7071 12.7071C13.0976 12.3166 13.0976 11.6834 12.7071 11.2929L11.4142 10L12.7071 8.70711C13.0976 8.31658 13.0976 7.68342 12.7071 7.29289C12.3166 6.90237 11.6834 6.90237 11.2929 7.29289L10 8.58579L8.70711 7.29289Z" fill="#d72c0d" />
              </svg>
              <h2 style={{ 
                margin: '0',
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#d72c0d' 
              }}>Connection Failed</h2>
            </div>
            <p style={{ 
              margin: '0 0 8px 0',
              color: '#212b36',
              fontSize: '14px' 
            }}>{error}</p>
            <p style={{ 
              margin: '0',
              color: '#637381',
              fontSize: '14px' 
            }}>You can close this window and try again.</p>
          </div>
        )}
      </div>
      
      <p style={{ 
        fontSize: '12px', 
        color: '#637381', 
        marginTop: '20px' 
      }}>
        This window will close automatically in a few seconds.
      </p>
    </div>
  );
}
